import { z } from "zod";
import { IsoTimestampSchema, paginatedResponse, UuidSchema } from "./common.js";
/**
 * Relay Answer module — wire-format Zod schemas.
 *
 * Shared by `@simpliwork/portal-api` and `@simpliwork/portal-frontend`.
 * snake_case on the wire (matches relay's other module schemas); camelCase
 * only on the TS-side types.
 *
 * The eight `answer_*` DB tables (PR #90) map roughly onto six wire entities:
 *
 *   documents      ↔ answer_documents (status: pending|processing|ready|failed)
 *   questionnaires ↔ answer_questionnaires + nested questions/responses/citations on the GET-by-id detail
 *   questions      ↔ answer_questions
 *   responses      ↔ answer_responses (renamed from QA's `answers` to avoid answer.answer_answers; type is still `Answer*Response*` for clarity)
 *   citations      ↔ answer_citations
 *   library        ↔ answer_library
 *
 * Document chunks + embeddings are *not* exposed via the wire — they're
 * orchestrator-internal. The Answer module never returns vector data.
 *
 * See:
 *   - docs/superpowers/specs/2026-05-20-answer-module-design.md
 *   - docs/superpowers/plans/2026-05-20-answer-module.md (M-ANSWER.2)
 */
// ── documents ──────────────────────────────────────────────────────────────
export const AnswerDocumentStatusSchema = z.enum(["pending", "processing", "ready", "failed"]);
export const AnswerDocumentResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    file_name: z.string(),
    mime_type: z.string(),
    storage_key: z.string(),
    title: z.string().nullable(),
    page_count: z.number().int().nonnegative(),
    content_hash: z.string().nullable(),
    status: AnswerDocumentStatusSchema,
    error_message: z.string().nullable(),
    file_size_bytes: z.number().int().nullable(),
    created_by: UuidSchema.nullable(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const AnswerDocumentListResponseSchema = paginatedResponse(AnswerDocumentResponseSchema);
// ── questionnaires ─────────────────────────────────────────────────────────
export const AnswerQuestionnaireStatusSchema = z.enum([
    "draft",
    "generating",
    "ready",
    "completed",
    "failed",
]);
export const AnswerQuestionnaireResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    title: z.string(),
    buyer_name: z.string().nullable(),
    status: AnswerQuestionnaireStatusSchema,
    created_by: UuidSchema.nullable(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
    completed_at: IsoTimestampSchema.nullable(),
});
export const AnswerQuestionnaireListResponseSchema = paginatedResponse(AnswerQuestionnaireResponseSchema);
export const CreateAnswerQuestionnaireRequestSchema = z.object({
    title: z.string().min(1).max(500),
    buyer_name: z.string().max(255).optional(),
});
// ── questions ──────────────────────────────────────────────────────────────
export const AnswerQuestionResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    questionnaire_id: UuidSchema,
    external_id: z.string(),
    section: z.string().nullable(),
    question_text: z.string(),
    sort_order: z.number().int().nonnegative(),
    created_at: IsoTimestampSchema,
});
// Bulk import payload — Answer's primary "add questions to questionnaire"
// motion. One row per question; `sort_order` is assigned client-side or by
// the server based on array index when omitted.
export const ImportAnswerQuestionsRequestSchema = z.object({
    questions: z
        .array(z.object({
        external_id: z.string().max(64).optional(),
        section: z.string().max(255).optional(),
        question_text: z.string().min(1).max(10_000),
        sort_order: z.number().int().nonnegative().optional(),
    }))
        .min(1)
        .max(500),
});
// ── responses (a.k.a. "answers" in the public API surface) ─────────────────
export const AnswerResponseConfidenceSchema = z.enum(["high", "medium", "low", "pending"]);
export const AnswerReviewStatusSchema = z.enum(["pending", "approved", "edited", "rejected"]);
export const AnswerResponseRecordSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    question_id: UuidSchema,
    draft_text: z.string(),
    final_text: z.string().nullable(),
    confidence: AnswerResponseConfidenceSchema,
    review_status: AnswerReviewStatusSchema,
    library_entry_id: UuidSchema.nullable(),
    model_id: z.string().nullable(),
    model_tier: z.string().nullable(),
    generated_at: IsoTimestampSchema.nullable(),
    approved_by: UuidSchema.nullable(),
    approved_at: IsoTimestampSchema.nullable(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const ReviewAnswerResponseRequestSchema = z.object({
    final_text: z.string().max(50_000).optional(),
    review_status: AnswerReviewStatusSchema.optional(),
    confidence: AnswerResponseConfidenceSchema.optional(),
});
// ── citations ──────────────────────────────────────────────────────────────
export const AnswerCitationResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    response_id: UuidSchema,
    document_id: UuidSchema,
    chunk_id: UuidSchema.nullable(),
    snippet: z.string(),
    page_start: z.number().int().nullable(),
    page_end: z.number().int().nullable(),
    sort_order: z.number().int().nonnegative(),
    created_at: IsoTimestampSchema,
});
// ── library ────────────────────────────────────────────────────────────────
// The library row's `question_embedding` is intentionally omitted from the
// wire — it's a 1536-d float vector that bloats responses with no operator
// value. RAG uses it server-side via pgvector; clients see only the text.
export const AnswerLibraryEntryResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    question_normalized: z.string(),
    approved_answer: z.string(),
    source_response_id: UuidSchema.nullable(),
    use_count: z.number().int().nonnegative(),
    last_used_at: IsoTimestampSchema.nullable(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const AnswerLibraryListResponseSchema = paginatedResponse(AnswerLibraryEntryResponseSchema);
export const PromoteAnswerLibraryRequestSchema = z.object({
    source_response_id: UuidSchema,
});
// ── questionnaire detail (hydrated) ────────────────────────────────────────
// `GET /v1/answer/questionnaires/:id` returns the parent row PLUS all its
// questions in sort_order with their (optional) response and citations
// inlined. Lighter than N+1 round-trips for the review UI, and the data
// stays consistent with `questions.length × ~3 fields` even on large
// questionnaires (500-question cap on import keeps this bounded).
export const AnswerQuestionWithResponseSchema = AnswerQuestionResponseSchema.extend({
    response: AnswerResponseRecordSchema.nullable(),
    citations: z.array(AnswerCitationResponseSchema),
});
export const AnswerQuestionnaireDetailResponseSchema = AnswerQuestionnaireResponseSchema.extend({
    questions: z.array(AnswerQuestionWithResponseSchema),
});
// ── generate (kick-off) ────────────────────────────────────────────────────
// POST /v1/answer/questionnaires/:id/generate — credit-check + Inngest send.
// Request body is empty; response carries the pre-flight credit estimate
// and the new status the questionnaire transitioned to.
export const GenerateAnswerQuestionnaireRequestSchema = z.object({}).strict();
export const GenerateAnswerQuestionnaireResponseSchema = z.object({
    questionnaire_id: UuidSchema,
    status: AnswerQuestionnaireStatusSchema,
    estimated_credits: z.number().int().nonnegative(),
    question_count: z.number().int().nonnegative(),
});
//# sourceMappingURL=answer.js.map